import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SOCKET_URL } from "../config";

/**
 * Chat socket.
 *
 * - Authenticates once the server greets with "hello" (never unprompted); the
 *   server ignores everything else until it answers "auth_ok".
 * - Reconnects forever with capped, jittered backoff, and immediately when the
 *   browser comes back online or the tab becomes visible.
 * - Heartbeat: "ping" every 25s; no traffic for 60s closes and reconnects.
 * - `connectionVersion` increments on every successful (re)authentication so pages
 *   can re-fetch whatever they may have missed while disconnected.
 * - `emitLocal` feeds an event to subscribers without the network, so the user who
 *   made a change sees it immediately from the REST response.
 */
const SocketContext = createContext({
  socket: null,
  isConnected: false,
  connectionVersion: 0,
  subscribe: () => {},
  unsubscribe: () => {},
  reconnect: () => {},
  socketSend: () => {},
  emitLocal: () => {},
});

const useSocket = () => useContext(SocketContext);

const PING_INTERVAL_MS = 25000;
const IDLE_TIMEOUT_MS = 60000;
const MAX_BACKOFF_MS = 30000;

const readToken = () => {
  try {
    const raw = localStorage.getItem("accessToken");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const SocketProvider = ({ children }) => {
  const [token, setToken] = useState(readToken);

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionVersion, setConnectionVersion] = useState(0);

  const subscribers = useRef([]);
  const wsRef = useRef(null);
  const tokenRef = useRef(token);
  const retryCount = useRef(0);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const lastSeenAt = useRef(0);
  const stopped = useRef(true);

  tokenRef.current = token;

  // Login/logout happen elsewhere and only touch localStorage; pick them up.
  useEffect(() => {
    const sync = () => {
      const next = readToken();
      setToken((prev) => (prev === next ? prev : next));
    };
    const interval = setInterval(sync, 2000);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const dispatch = useCallback((data) => {
    subscribers.current.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error("Socket subscriber failed:", error);
      }
    });
  }, []);

  const clearTimers = () => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
  };

  const closeCurrent = () => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    }
    setIsConnected(false);
  };

  const connect = useCallback(() => {
    clearTimers();
    closeCurrent();
    if (stopped.current || !tokenRef.current) return;

    const ws = new WebSocket(SOCKET_URL);
    wsRef.current = ws;
    setSocket(ws);

    const scheduleReconnect = () => {
      if (stopped.current || wsRef.current !== ws) return;
      wsRef.current = null;
      setIsConnected(false);
      clearTimers();
      retryCount.current += 1;
      const base = Math.min(1000 * 2 ** Math.min(retryCount.current, 5), MAX_BACKOFF_MS);
      const delay = base / 2 + Math.random() * (base / 2);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onopen = () => {
      lastSeenAt.current = Date.now();
      pingTimer.current = setInterval(() => {
        if (Date.now() - lastSeenAt.current > IDLE_TIMEOUT_MS) {
          ws.close();
          scheduleReconnect();
          return;
        }
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      lastSeenAt.current = Date.now();
      let data;
      try {
        data = JSON.parse(event?.data);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
        return;
      }

      // Only a server that greets with "hello" understands auth. Never send the token
      // unprompted: the old server rebroadcast unknown messages to every client.
      if (data?.type === "hello") {
        if (tokenRef.current) {
          ws.send(JSON.stringify({ type: "auth", token: tokenRef.current }));
        }
        return;
      }
      if (data?.type === "auth_ok") {
        retryCount.current = 0;
        setIsConnected(true);
        setConnectionVersion((v) => v + 1);
        return;
      }
      if (data?.type === "pong") return;

      dispatch(data);
    };

    ws.onclose = scheduleReconnect;
    ws.onerror = () => {
      console.warn("WebSocket error");
    };
  }, [dispatch]);

  const reconnect = useCallback(() => {
    retryCount.current = 0;
    connect();
  }, [connect]);

  // Connect while logged in; tear down on logout/unmount.
  useEffect(() => {
    if (token) {
      stopped.current = false;
      connect();
    } else {
      stopped.current = true;
      clearTimers();
      closeCurrent();
      setSocket(null);
    }
    return () => {
      stopped.current = true;
      clearTimers();
      closeCurrent();
    };
  }, [token, connect]);

  // Come back immediately when the network or the tab returns. After sleep a socket
  // can still report OPEN while dead, so a long silence also forces a reconnect.
  useEffect(() => {
    const wake = () => {
      if (stopped.current) return;
      const ws = wsRef.current;
      const silentFor = Date.now() - lastSeenAt.current;
      if (
        !ws ||
        ws.readyState === WebSocket.CLOSED ||
        ws.readyState === WebSocket.CLOSING ||
        (ws.readyState === WebSocket.OPEN && silentFor > IDLE_TIMEOUT_MS)
      ) {
        reconnect();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") wake();
    };
    // The old socket is dead once the network drops, even if it still reports OPEN.
    const onOffline = () => {
      if (stopped.current) return;
      clearTimers();
      closeCurrent();
    };
    const onOnline = () => {
      if (!stopped.current) reconnect();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reconnect]);

  const subscribe = useCallback((callback) => {
    subscribers.current.push(callback);
  }, []);

  const unsubscribe = useCallback((callback) => {
    subscribers.current = subscribers.current.filter(
      (subscriber) => subscriber !== callback
    );
  }, []);

  /**
   * Only typing and read events are sent from the client. They are not worth
   * queueing: a reconnect re-fetches the open chat, which marks it read server-side.
   */
  const socketSend = useCallback((data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(typeof data === "string" ? data : JSON.stringify(data));
        return true;
      } catch (error) {
        console.error("WebSocket Send Error:", error);
      }
    }
    return false;
  }, []);

  const emitLocal = useCallback((data) => dispatch(data), [dispatch]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        connectionVersion,
        subscribe,
        unsubscribe,
        reconnect,
        socketSend,
        emitLocal,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider, useSocket };
